import { useCallback, useEffect, useMemo, useRef } from "react";

export function useMfaAttempt() {
  const tokenRef = useRef(0);
  const attemptRef = useRef<number | null>(null);
  const inFlightRef = useRef(new Map<number, true>());
  const forgottenRef = useRef(new Set<number>());
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      attemptRef.current = null;
    };
  }, []);

  const isCurrent = useCallback(
    (token: number) => !unmountedRef.current && attemptRef.current === token,
    [],
  );

  const begin = useCallback(() => {
    tokenRef.current += 1;
    attemptRef.current = tokenRef.current;
    return tokenRef.current;
  }, []);

  const markInFlight = useCallback((token: number) => {
    inFlightRef.current.set(token, true);
  }, []);

  const forgetInFlight = useCallback(() => {
    for (const token of inFlightRef.current.keys()) forgottenRef.current.add(token);
  }, []);

  const abandon = useCallback(() => {
    forgetInFlight();
    attemptRef.current = null;
  }, [forgetInFlight]);

  const settle = useCallback((token: number) => {
    inFlightRef.current.delete(token);
    const forgotten = forgottenRef.current.delete(token);
    return !forgotten && !unmountedRef.current && attemptRef.current === token;
  }, []);

  return useMemo(
    () => ({ begin, markInFlight, isCurrent, forgetInFlight, abandon, settle }),
    [begin, markInFlight, isCurrent, forgetInFlight, abandon, settle],
  );
}
